import { useIsLoggedInRetailer } from "@faire/marketplace-lib/serialized-data/isLoggedInRetailer";
import { useQuery as useGetConductorComponentFromToken } from "@faire/web-api--source/endpoints/www/api/conductor/component/componentToken/get-hooks";
import { QueryParameters as ConductorComponentQueryParameters } from "@faire/web-api--source/endpoints/www/api/conductor/component/get";
import { useQuery as useGetConductorComponentQuery } from "@faire/web-api--source/endpoints/www/api/conductor/component/get-hooks";
import { useInvalidateQuery as useInvalidateConductorComponentQuery } from "@faire/web-api--source/endpoints/www/api/conductor/component/get-hooks";
import { useCallback } from "react";

import { IGetConductorComponentProps, IConductorComponentData } from "./types";
import { useConductorInvalidationListenerEffect } from "./useConductorInvalidationListenerEffect";

export const useConductorComponent = ({
  componentToken,
  listenForBackendInvalidation,
  ...parameters
}: IGetConductorComponentProps): IConductorComponentData => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const queryParams = ConductorComponentQueryParameters.build({
    ...parameters,
  });

  const conductorComponentFromToken = useGetConductorComponentFromToken(
    componentToken ?? "",
    {
      enabled: !!componentToken,
    }
  );

  const conductorComponentFallback = useGetConductorComponentQuery(
    queryParams,
    {
      enabled: isLoggedInRetailer && !!queryParams,
      staleTime: Infinity,
      gcTime: Infinity,
    }
  );

  const invalidateConductorComponentQueryBase =
    useInvalidateConductorComponentQuery();

  const invalidateConductorComponentQuery = useCallback(() => {
    return invalidateConductorComponentQueryBase(queryParams, undefined, {
      refetchType: "active",
    });
  }, [invalidateConductorComponentQueryBase, queryParams]);

  useConductorInvalidationListenerEffect({
    invalidationCallback: invalidateConductorComponentQuery,
    listenForBackendInvalidation,
    surface: parameters.surface,
  });

  const activeConductorComponent = componentToken
    ? conductorComponentFromToken
    : conductorComponentFallback;
  const { data, isPending } = activeConductorComponent;

  const conductorSduiViewLayout = data?.component?.sdui_component?.view_layout;
  const conductorBuilderModelName =
    data?.component?.builder_component?.builder_model_name;
  const shouldShowConductor =
    !!conductorSduiViewLayout || !!conductorBuilderModelName;
  // feature name is used in useSiteBannersList hook to identify banner type
  const featureName = data?.component?.feature_name;

  // This is used for when multiple components are present in the SDUI layout.
  // This is to identify the feature name of the component that is being rendered
  // so that we can sort them correctly.

  const componentFeatureNames = data?.component?.child_component_feature_names;

  return {
    componentToken: data?.component?.token,
    shouldShowConductor: shouldShowConductor,
    conductorSduiViewLayout: conductorSduiViewLayout,
    conductorBuilderModelName: conductorBuilderModelName,
    isConductorComponentPending: isPending,
    featureName,
    componentFeatureNames,
    invalidateConductorComponentQuery,
  };
};
